import { useState, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { stringify } from 'qs';
import outputs from 'root/cfOutput';
import { LanguageService } from 'root/src/client/v2/common/helpers/language.service';
import { isLocalhost } from 'root/src/shared/util/envSelect';
import { WIDGET_Z_INDEX } from 'root/src/client/v2/layout/fixtures/layout.fixtures';
const { domainName } = outputs;
const MIN_WIDTH = 280;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 400;
const ASPECT_RATIO = 16 / 9;
const EDGE_MARGIN = 16;
const getTwitchEmbedParent = () => {
    if (isLocalhost)
        return 'localhost';
    const languageCode = LanguageService.getDomainPrefixCode(window.location.host);
    return languageCode ? `${languageCode}.${domainName}` : domainName;
};
const getTwitchPlayerUrl = (channelName) => {
    const query = stringify({
        channel: channelName,
        parent: getTwitchEmbedParent(),
        muted: true,
    });
    return `https://player.twitch.tv/?${query}`;
};
const isTouchDevice = () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
const useResizable = () => {
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const [isResizing, setIsResizing] = useState(false);
    const startXRef = useRef(0);
    const startWidthRef = useRef(DEFAULT_WIDTH);
    const onPointerDown = useCallback((e) => {
        if (isTouchDevice())
            return;
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        setIsResizing(true);
        startXRef.current = e.clientX;
        startWidthRef.current = width;
    }, [width]);
    const onPointerMove = useCallback((e) => {
        if (!isResizing)
            return;
        const delta = startXRef.current - e.clientX;
        const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidthRef.current + delta));
        setWidth(newWidth);
    }, [isResizing]);
    const onPointerUp = useCallback(() => {
        setIsResizing(false);
    }, []);
    return { width, isResizing, onPointerDown, onPointerMove, onPointerUp };
};
const getSnapCorner = (position, playerWidth) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const playerHeight = playerWidth / ASPECT_RATIO + 72;
    const left = vw - position.right - playerWidth;
    const top = vh - position.bottom - playerHeight;
    const centerX = vw / 2;
    const centerY = vh / 2;
    return {
        right: left + playerWidth / 2 < centerX ? vw - playerWidth - EDGE_MARGIN : EDGE_MARGIN,
        bottom: top + playerHeight / 2 < centerY ? vh - playerHeight - EDGE_MARGIN : EDGE_MARGIN,
    };
};
const useDraggable = (initialPosition, playerWidth) => {
    const [position, setPosition] = useState(initialPosition);
    const [isDragging, setIsDragging] = useState(false);
    const [isSnapping, setIsSnapping] = useState(false);
    const startPointerRef = useRef({ x: 0, y: 0 });
    const startPositionRef = useRef(initialPosition);
    const onDragPointerDown = useCallback((e) => {
        if (isTouchDevice())
            return;
        if (e.target.closest('button'))
            return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setIsDragging(true);
        setIsSnapping(false);
        startPointerRef.current = { x: e.clientX, y: e.clientY };
        startPositionRef.current = position;
    }, [position]);
    const onDragPointerMove = useCallback((e) => {
        if (!isDragging)
            return;
        const deltaX = e.clientX - startPointerRef.current.x;
        const deltaY = e.clientY - startPointerRef.current.y;
        const maxRight = window.innerWidth - playerWidth - EDGE_MARGIN;
        const playerHeight = playerWidth / ASPECT_RATIO + 72;
        const maxBottom = window.innerHeight - playerHeight - EDGE_MARGIN;
        setPosition({
            right: Math.max(EDGE_MARGIN, Math.min(maxRight, startPositionRef.current.right - deltaX)),
            bottom: Math.max(EDGE_MARGIN, Math.min(maxBottom, startPositionRef.current.bottom - deltaY)),
        });
    }, [isDragging, playerWidth]);
    const onDragPointerUp = useCallback(() => {
        if (!isDragging)
            return;
        setIsDragging(false);
        setIsSnapping(true);
        setPosition(prev => getSnapCorner(prev, playerWidth));
    }, [isDragging, playerWidth]);
    const onSnapTransitionEnd = useCallback(() => {
        setIsSnapping(false);
    }, []);
    return {
        position,
        isDragging,
        isSnapping,
        onDragPointerDown,
        onDragPointerMove,
        onDragPointerUp,
        onSnapTransitionEnd,
    };
};
const getDefaultViewState = () => (isTouchDevice() ? 'bar' : 'expanded');
const PlayerBar = ({ channelName, stream, onMinimize, onExpand, dragHandlers }) => (<div className="flex items-center justify-between gap-2 px-3 py-2 bg-primary md:cursor-grab md:active:cursor-grabbing select-none" onPointerDown={dragHandlers.onPointerDown} onPointerMove={dragHandlers.onPointerMove} onPointerUp={dragHandlers.onPointerUp}>
		<div className="flex items-center gap-2 min-w-0 overflow-hidden cursor-pointer" onClick={onExpand}>
			<span className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0"/>
			<span className="text-white text-sm font-bold truncate max-w-[45%]">{channelName}</span>
			{stream.gameName && (<span className="text-white/60 text-xs truncate">{stream.gameName}</span>)}
		</div>
		<button type="button" onClick={onMinimize} className="text-white/60 hover:text-white text-lg leading-none px-1 flex-shrink-0 cursor-pointer transition-colors" aria-label="Minimize player">
			&times;
		</button>
	</div>);
const PlayerFooter = ({ stream }) => (<div className="flex items-center gap-2 px-3 py-2 bg-primary min-w-0 overflow-hidden">
		<span className="text-white/60 text-xs flex-shrink-0">
			{new Intl.NumberFormat('en-US').format(stream.viewerCount)} viewers
		</span>
		<span className="text-white/40 text-xs flex-shrink-0">|</span>
		<span className="text-white/80 text-xs truncate">{stream.title}</span>
	</div>);
export const FloatingTwitchPlayer = ({ channelName, stream }) => {
    const [viewState, setViewState] = useState(getDefaultViewState);
    const { width, isResizing, onPointerDown, onPointerMove, onPointerUp } = useResizable();
    const { position, isDragging, isSnapping, onDragPointerDown, onDragPointerMove, onDragPointerUp, onSnapTransitionEnd, } = useDraggable({ right: EDGE_MARGIN, bottom: EDGE_MARGIN }, width);
    const isInteracting = isResizing || isDragging;
    const handleMinimize = useCallback(() => {
        setViewState((prev) => {
            if (prev === 'expanded')
                return 'bar';
            return 'collapsed';
        });
    }, []);
    const handleExpand = useCallback(() => {
        setViewState('expanded');
    }, []);
    const dragHandlers = {
        onPointerDown: onDragPointerDown,
        onPointerMove: onDragPointerMove,
        onPointerUp: onDragPointerUp,
    };
    return (<div className="fixed" style={{
            zIndex: WIDGET_Z_INDEX,
            right: position.right,
            bottom: position.bottom,
            transition: isSnapping ? 'right 0.3s cubic-bezier(0.25, 1, 0.5, 1), bottom 0.3s cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
        }} onTransitionEnd={onSnapTransitionEnd}>
			<AnimatePresence mode="wait">
				{viewState === 'collapsed' && (<motion.button key="collapsed" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} onClick={handleExpand} className="flex items-center gap-1.5 bg-red-600 text-white text-sm font-bold px-3 py-2 rounded-lg shadow-lg cursor-pointer hover:bg-red-700 transition-colors">
						<span className="w-2 h-2 bg-white rounded-full animate-pulse"/>
						LIVE
					</motion.button>)}
				{viewState === 'bar' && (<motion.div key="bar" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="flex flex-col bg-dark-4 rounded-lg shadow-2xl overflow-hidden max-w-[calc(100vw-2rem)]" style={{ width }}>
						<PlayerBar channelName={channelName} stream={stream} onMinimize={handleMinimize} onExpand={handleExpand} dragHandlers={dragHandlers}/>
						<PlayerFooter stream={stream}/>
					</motion.div>)}
				{viewState === 'expanded' && (<motion.div key="expanded" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="flex flex-col bg-dark-4 rounded-lg shadow-2xl overflow-hidden max-w-[calc(100vw-2rem)]" style={{ width }}>
						<div className="absolute top-0 left-0 w-3 h-full cursor-ew-resize group touch-none hidden md:block" style={{ zIndex: 1 }} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
							<div className="absolute top-1/2 left-0.5 -translate-y-1/2 w-1 h-8 rounded-full bg-white/0 group-hover:bg-white/40 transition-colors"/>
						</div>
						<PlayerBar channelName={channelName} stream={stream} onMinimize={handleMinimize} onExpand={handleExpand} dragHandlers={dragHandlers}/>
						<div className="relative w-full" style={{ height: width / ASPECT_RATIO }}>
							<iframe title={`${channelName} live stream`} src={getTwitchPlayerUrl(channelName)} className="absolute inset-0 w-full h-full" style={isInteracting ? { pointerEvents: 'none' } : undefined} allowFullScreen allow="autoplay; encrypted-media"/>
						</div>
						<PlayerFooter stream={stream}/>
					</motion.div>)}
			</AnimatePresence>
		</div>);
};
